import React, { useEffect, useMemo, useState } from "react";
import { api, API } from "../lib/apiClient";
import { useAuth } from "../context/AuthContext";
import { PRIORITIES, PRIORITY_ORDER, fmtDate } from "../lib/constants";
import { PriorityBadge } from "../components/Badges";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import CatalogField from "../components/CatalogField";
import { toast } from "sonner";
import { Send, Plus, Check, X, Trash2, Clock, CheckCircle2, XCircle, Paperclip, Upload, Download, Eye, Loader2 } from "lucide-react";

const EMPTY = { title: "", description: "", impacto: "", sistema: "", modulo: "", submodulo: "", departamento_solicitante: "", solicitante: "", priority: "media" };
const fmtBytes = (n) => (n > 1024 * 1024 ? `${(n / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`);
const isImage = (ct) => (ct || "").startsWith("image/");
const isPdf = (ct) => (ct || "").includes("pdf");

const ESTADO = {
  pendiente_aprobacion: { label: "Pendiente de aprobación", cls: "bg-amber-50 text-amber-700 border-amber-200", icon: Clock },
  aprobada: { label: "Aprobada", cls: "bg-green-50 text-green-700 border-green-200", icon: CheckCircle2 },
  rechazada: { label: "Rechazada", cls: "bg-red-50 text-red-700 border-red-200", icon: XCircle },
};

export default function Solicitudes() {
  const { user } = useAuth();
  const isPM = user?.role === "pm";
  const canCreate = user?.role === "stakeholder" || user?.role === "qa";

  const [items, setItems] = useState([]);
  const [catalog, setCatalog] = useState({ sistemas: [] });
  const [devs, setDevs] = useState([]);
  const [tab, setTab] = useState(isPM ? "pendientes" : "todas");
  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [stagedFiles, setStagedFiles] = useState([]);
  const [rejectFor, setRejectFor] = useState(null);
  const [motivo, setMotivo] = useState("");

  const load = () => api.get("/solicitudes").then((r) => setItems(r.data));
  useEffect(() => {
    load();
    api.get("/catalogs").then((r) => setCatalog(r.data)).catch(() => {});
    api.get("/developments").then((r) => setDevs(r.data)).catch(() => {});
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // Mismas opciones que se han guardado en Desarrollos
  const sistemaOptions = useMemo(() => Array.from(new Set([
    ...(catalog.sistemas || []).map((s) => s.name),
    ...devs.map((d) => d.sistema).filter(Boolean),
  ])), [catalog, devs]);
  const sistemaObj = (catalog.sistemas || []).find((s) => s.name === form.sistema);
  const moduloOptions = useMemo(() => Array.from(new Set([
    ...((sistemaObj?.modulos || []).map((m) => m.name)),
    ...devs.filter((d) => d.sistema === form.sistema).map((d) => d.modulo).filter(Boolean),
  ])), [sistemaObj, devs, form.sistema]);
  const moduloObj = (sistemaObj?.modulos || []).find((m) => m.name === form.modulo);
  const submoduloOptions = useMemo(() => Array.from(new Set([
    ...(moduloObj?.submodulos || []),
    ...devs.filter((d) => d.sistema === form.sistema && d.modulo === form.modulo).map((d) => d.submodulo).filter(Boolean),
  ])), [moduloObj, devs, form.sistema, form.modulo]);
  const departamentoOptions = useMemo(() => Array.from(new Set([
    ...(catalog.departamentos || []),
    ...devs.map((d) => d.departamento_solicitante).filter(Boolean),
  ])).sort(), [catalog, devs]);
  const solicitanteOptions = useMemo(() => Array.from(new Set([
    ...(catalog.solicitantes || []),
    ...devs.map((d) => d.solicitante).filter(Boolean),
  ])).sort(), [catalog, devs]);

  const filtered = useMemo(() => {
    if (isPM && tab === "pendientes") return items.filter((s) => s.estado === "pendiente_aprobacion");
    return items;
  }, [items, isPM, tab]);

  const pendingCount = items.filter((s) => s.estado === "pendiente_aprobacion").length;

  const submit = async () => {
    if (!form.title.trim()) { toast.error("El título es obligatorio"); return; }
    setSaving(true);
    try {
      const { data } = await api.post("/solicitudes", form);
      const sid = data.id;
      if (sid && stagedFiles.length) {
        const token = localStorage.getItem("devflow_token");
        for (const file of stagedFiles) {
          if (file.size > 25 * 1024 * 1024) { toast.error(`${file.name} supera 25 MB`); continue; }
          const fd = new FormData(); fd.append("file", file);
          const res = await fetch(`${API}/solicitudes/${sid}/attachments`, { method: "POST", headers: { Authorization: `Bearer ${token}` }, body: fd });
          if (!res.ok) toast.error(`No se pudo subir ${file.name}`);
        }
      }
      toast.success("Solicitud enviada para aprobación");
      setCreateOpen(false); setForm(EMPTY); setStagedFiles([]); load();
    } catch (err) { toast.error("No se pudo enviar la solicitud"); }
    finally { setSaving(false); }
  };

  const aprobar = async (s) => {
    try { const { data } = await api.post(`/solicitudes/${s.id}/aprobar`); toast.success(`Aprobada · se creó ${data.dev_code} en Desarrollos`); load(); }
    catch (err) { toast.error(err.response?.data?.detail || "No se pudo aprobar"); }
  };
  const rechazar = async () => {
    if (!motivo.trim()) { toast.error("Indica el motivo del rechazo"); return; }
    try { await api.post(`/solicitudes/${rejectFor.id}/rechazar`, { motivo }); toast.success("Solicitud rechazada"); setRejectFor(null); setMotivo(""); load(); }
    catch (err) { toast.error("No se pudo rechazar"); }
  };
  const eliminar = async (s) => {
    if (!window.confirm("¿Eliminar esta solicitud?")) return;
    try { await api.delete(`/solicitudes/${s.id}`); toast.success("Eliminada"); load(); }
    catch (err) { toast.error(err.response?.data?.detail || "No se pudo eliminar"); }
  };

  return (
    <div>
      <div className="bg-white border-b border-border px-8 py-6 flex items-end justify-between gap-4">
        <div>
          <p className="text-[11px] uppercase tracking-[0.2em] text-slate-400">Requerimientos</p>
          <h1 className="font-heading text-3xl font-extrabold tracking-tight mt-0.5 flex items-center gap-2">
            <Send className="h-7 w-7" /> Solicitudes de Requerimientos
          </h1>
        </div>
        {canCreate && (
          <Button className="rounded-sm h-9 bg-slate-900 hover:bg-slate-800 text-white" onClick={() => { setForm(EMPTY); setStagedFiles([]); setCreateOpen(true); }} data-testid="sol-new-btn">
            <Plus className="h-4 w-4 mr-1.5" />Nueva solicitud
          </Button>
        )}
      </div>

      <div className="p-8 space-y-5">
        {isPM && (
          <div className="flex items-center gap-2">
            <button onClick={() => setTab("pendientes")} data-testid="sol-tab-pendientes"
              className={`px-3 py-1.5 rounded-sm text-sm font-medium border ${tab === "pendientes" ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-300"}`}>
              Por aprobar {pendingCount > 0 && <span className="ml-1 font-mono">({pendingCount})</span>}
            </button>
            <button onClick={() => setTab("todas")} data-testid="sol-tab-todas"
              className={`px-3 py-1.5 rounded-sm text-sm font-medium border ${tab === "todas" ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-300"}`}>
              Todas
            </button>
          </div>
        )}

        {filtered.length === 0 ? (
          <div className="text-center py-16 text-slate-400 border border-dashed border-slate-200 rounded-sm">
            {canCreate ? "Aún no tienes solicitudes. Crea una con \u201cNueva solicitud\u201d." : "No hay solicitudes."}
          </div>
        ) : (
          <div className="space-y-3">
            {filtered.map((s) => {
              const est = ESTADO[s.estado] || ESTADO.pendiente_aprobacion;
              const EIcon = est.icon;
              return (
                <div key={s.id} data-testid={`sol-card-${s.id}`} className="bg-white border border-border rounded-sm p-4">
                  <div className="flex items-start justify-between gap-4">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className={`inline-flex items-center gap-1 rounded-sm border px-2 py-0.5 text-[11px] font-bold uppercase tracking-wide ${est.cls}`}>
                          <EIcon className="h-3 w-3" />{est.label}{s.estado === "aprobada" && s.dev_code ? ` · ${s.dev_code}` : ""}
                        </span>
                        <PriorityBadge priority={s.priority} />
                      </div>
                      <h3 className="font-semibold text-lg mt-1.5">{s.title}</h3>
                      {s.description && <p className="text-sm text-slate-600 mt-1 whitespace-pre-wrap">{s.description}</p>}
                      {s.impacto && <p className="text-sm text-slate-500 mt-1"><span className="font-semibold text-slate-400 uppercase text-[10px] tracking-wider">Impacto:</span> {s.impacto}</p>}
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-6 gap-y-1 mt-3 text-xs text-slate-500">
                        {(s.sistema || s.modulo) && <div><span className="text-slate-400">Sistema/Módulo:</span> {[s.sistema, s.modulo, s.submodulo].filter(Boolean).join(" / ")}</div>}
                        {s.departamento_solicitante && <div><span className="text-slate-400">Departamento:</span> {s.departamento_solicitante}</div>}
                        {s.solicitante && <div><span className="text-slate-400">Solicitante:</span> {s.solicitante}</div>}
                        <div><span className="text-slate-400">Creada por:</span> {s.created_by_name} · {fmtDate(s.created_at)}</div>
                      </div>
                      {(s.attachments || []).length > 0 && <SolAttachments solId={s.id} attachments={s.attachments} />}
                      {s.estado === "rechazada" && s.motivo_rechazo && (
                        <p className="text-sm text-red-600 mt-2 bg-red-50 border border-red-200 rounded-sm px-3 py-1.5"><span className="font-semibold">Motivo del rechazo:</span> {s.motivo_rechazo}</p>
                      )}
                    </div>
                    <div className="flex flex-col gap-2 shrink-0">
                      {isPM && s.estado === "pendiente_aprobacion" && (
                        <>
                          <Button className="rounded-sm h-8 bg-green-600 hover:bg-green-700 text-white" onClick={() => aprobar(s)} data-testid={`sol-approve-${s.id}`}><Check className="h-4 w-4 mr-1" />Aprobar</Button>
                          <Button variant="outline" className="rounded-sm h-8 border-red-300 text-red-600 hover:bg-red-50" onClick={() => { setRejectFor(s); setMotivo(""); }} data-testid={`sol-reject-${s.id}`}><X className="h-4 w-4 mr-1" />Rechazar</Button>
                        </>
                      )}
                      {!isPM && s.estado === "pendiente_aprobacion" && (
                        <Button variant="ghost" className="rounded-sm h-8 text-slate-400 hover:text-red-600" onClick={() => eliminar(s)} data-testid={`sol-delete-${s.id}`}><Trash2 className="h-4 w-4" /></Button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Crear solicitud */}
      <Dialog open={createOpen} onOpenChange={(o) => !saving && setCreateOpen(o)}>
        <DialogContent className="rounded-sm max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="sol-create-dialog">
          <DialogHeader><DialogTitle>Nueva solicitud de requerimiento</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Solicitado por</Label>
                <Input data-testid="sol-created-by" value={user?.name || ""} readOnly disabled className="mt-1.5 rounded-sm bg-slate-50 text-slate-600" />
              </div>
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Fecha de solicitud</Label>
                <Input data-testid="sol-created-at" value={fmtDate(new Date().toISOString())} readOnly disabled className="mt-1.5 rounded-sm bg-slate-50 text-slate-600" />
              </div>
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Título *</Label>
              <Input data-testid="sol-title" value={form.title} onChange={(e) => set("title", e.target.value)} className="mt-1.5 rounded-sm" />
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Descripción</Label>
              <Textarea data-testid="sol-description" value={form.description} onChange={(e) => set("description", e.target.value)} rows={4} className="mt-1.5 rounded-sm" />
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Impacto</Label>
              <Textarea data-testid="sol-impacto" value={form.impacto} onChange={(e) => set("impacto", e.target.value)} rows={3} placeholder="Describe el impacto del requerimiento" className="mt-1.5 rounded-sm" />
            </div>
            <div className="grid grid-cols-3 gap-4">
              <CatalogField label="Sistema" testid="sol-sistema-select" value={form.sistema} options={sistemaOptions} allowAdd={false} onChange={(v) => setForm((f) => ({ ...f, sistema: v, modulo: "", submodulo: "" }))} />
              <CatalogField label="Módulo" testid="sol-modulo-select" value={form.modulo} options={moduloOptions} allowAdd={false} disabled={!form.sistema} onChange={(v) => setForm((f) => ({ ...f, modulo: v, submodulo: "" }))} />
              <CatalogField label="Submódulo" testid="sol-submodulo-select" value={form.submodulo} options={submoduloOptions} allowAdd={false} disabled={!form.modulo} onChange={(v) => setForm((f) => ({ ...f, submodulo: v }))} />
            </div>
            <div className="grid grid-cols-3 gap-4">
              <CatalogField label="Departamento solicitante" testid="sol-departamento-select" value={form.departamento_solicitante} options={departamentoOptions} allowAdd={false} onChange={(v) => setForm((f) => ({ ...f, departamento_solicitante: v }))} />
              <CatalogField label="Solicitante" testid="sol-solicitante-select" value={form.solicitante} options={solicitanteOptions} allowAdd={false} onChange={(v) => setForm((f) => ({ ...f, solicitante: v }))} />
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Prioridad</Label>
                <Select value={form.priority} onValueChange={(v) => set("priority", v)}>
                  <SelectTrigger data-testid="sol-priority" className="mt-1.5 rounded-sm"><SelectValue /></SelectTrigger>
                  <SelectContent>{PRIORITY_ORDER.map((p) => <SelectItem key={p} value={p}>{PRIORITIES[p].label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>

            <div className="border-t border-slate-100 pt-4">
              <div className="flex items-center justify-between">
                <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500 flex items-center gap-2"><Paperclip className="h-4 w-4" /> Archivos adjuntos {stagedFiles.length > 0 && <span className="font-mono text-slate-400">({stagedFiles.length})</span>}</Label>
                <label className="cursor-pointer inline-flex items-center gap-1.5 text-sm font-medium text-slate-700 border border-slate-300 rounded-sm px-3 h-8 hover:bg-slate-50" data-testid="sol-attach-upload">
                  <Upload className="h-4 w-4" /> Agregar archivos
                  <input type="file" multiple className="hidden" onChange={(e) => { setStagedFiles((prev) => [...prev, ...Array.from(e.target.files)]); e.target.value = ""; }} />
                </label>
              </div>
              {stagedFiles.length === 0 ? (
                <p className="text-xs text-slate-400 mt-2">Puedes adjuntar imágenes, PDF u otros documentos (máx. 25 MB c/u).</p>
              ) : (
                <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {stagedFiles.map((f, i) => (
                    <div key={`${f.name}-${i}`} className="flex items-center gap-2 border border-slate-200 rounded-sm px-3 py-2" data-testid={`sol-staged-${i}`}>
                      <Paperclip className="h-4 w-4 text-slate-400 shrink-0" />
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium truncate" title={f.name}>{f.name}</p>
                        <p className="text-[11px] text-slate-400">{fmtBytes(f.size || 0)}</p>
                      </div>
                      <button type="button" onClick={() => setStagedFiles((prev) => prev.filter((_, idx) => idx !== i))} className="text-slate-300 hover:text-red-600 p-1" title="Quitar" data-testid={`sol-staged-del-${i}`}><Trash2 className="h-4 w-4" /></button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          <div className="flex justify-end gap-2 mt-4">
            <Button variant="outline" className="rounded-sm" onClick={() => setCreateOpen(false)} disabled={saving}>Cancelar</Button>
            <Button className="rounded-sm bg-slate-900 hover:bg-slate-800 text-white" onClick={submit} disabled={saving} data-testid="sol-submit">{saving ? "Enviando..." : "Enviar solicitud"}</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Rechazar */}
      <Dialog open={!!rejectFor} onOpenChange={(o) => !o && setRejectFor(null)}>
        <DialogContent className="rounded-sm max-w-md" data-testid="sol-reject-dialog">
          <DialogHeader><DialogTitle>Rechazar solicitud</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-500 -mt-2">{rejectFor?.title}</p>
          <div className="mt-2">
            <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Motivo del rechazo *</Label>
            <Textarea data-testid="sol-reject-motivo" value={motivo} onChange={(e) => setMotivo(e.target.value)} rows={4} className="mt-1.5 rounded-sm" />
          </div>
          <div className="flex justify-end gap-2 mt-4">
            <Button variant="outline" className="rounded-sm" onClick={() => setRejectFor(null)}>Cancelar</Button>
            <Button className="rounded-sm bg-red-600 hover:bg-red-700 text-white" onClick={rechazar} data-testid="sol-reject-confirm">Rechazar solicitud</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function SolAttachments({ solId, attachments }) {
  const [busy, setBusy] = useState(null);
  const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem("devflow_token")}` });
  const fetchBlob = async (att) => {
    const res = await fetch(`${API}/solicitudes/${solId}/attachments/${att.id}`, { headers: authHeaders() });
    if (!res.ok) throw new Error("fetch failed");
    return await res.blob();
  };
  const open = async (att, forceDownload) => {
    setBusy(att.id);
    try {
      const blob = await fetchBlob(att);
      const url = URL.createObjectURL(blob);
      if (!forceDownload && (isImage(att.content_type) || isPdf(att.content_type))) {
        window.open(url, "_blank");
      } else {
        const a = document.createElement("a"); a.href = url; a.download = att.filename; a.click();
      }
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch { toast.error("No se pudo abrir el adjunto"); }
    finally { setBusy(null); }
  };
  return (
    <div className="mt-3" data-testid={`sol-attachments-${solId}`}>
      <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 flex items-center gap-1.5"><Paperclip className="h-3 w-3" /> Adjuntos ({attachments.length})</p>
      <div className="mt-1.5 flex flex-wrap gap-2">
        {attachments.map((att) => (
          <div key={att.id} className="flex items-center gap-2 border border-slate-200 rounded-sm px-2.5 py-1.5 bg-slate-50" data-testid={`sol-attach-${att.id}`}>
            {isImage(att.content_type) ? <Eye className="h-3.5 w-3.5 text-slate-400" /> : <Paperclip className="h-3.5 w-3.5 text-slate-400" />}
            <span className="text-xs text-slate-700 max-w-[180px] truncate" title={att.filename}>{att.filename}</span>
            <span className="text-[10px] text-slate-400">{fmtBytes(att.size || 0)}</span>
            <button type="button" onClick={() => open(att, false)} disabled={busy === att.id} className="text-slate-400 hover:text-slate-800 p-0.5" title="Ver / Abrir" data-testid={`sol-attach-view-${att.id}`}>
              {busy === att.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Eye className="h-3.5 w-3.5" />}
            </button>
            <button type="button" onClick={() => open(att, true)} className="text-slate-400 hover:text-slate-800 p-0.5" title="Descargar" data-testid={`sol-attach-dl-${att.id}`}><Download className="h-3.5 w-3.5" /></button>
          </div>
        ))}
      </div>
    </div>
  );
}
